import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, AlertTriangle, Download, Mail, Volume2, VolumeX } from "lucide-react";
import api, { formatApiError, cachedGet, apiCache } from "@/lib/api";
import { savePdf } from "@/lib/savePdf";
import { shareOrMailtoPdf } from "@/lib/sharePdf";
import { toast } from "sonner";
import EmailRecipientDialog from "@/components/EmailRecipientDialog";
import FieldHelp from "@/components/FieldHelp";
import { useAuth } from "@/contexts/AuthContext";

const PRIORITIES = ["low", "medium", "high"];

export default function IncidentsPage() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(null);     // incident id currently loading
  const [playingId, setPlayingId] = useState(null); // incident id currently playing
  const [audio, setAudio] = useState(null);         // active HTMLAudioElement
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), priority: "medium", title: "", description: "", action_taken: "" });
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const load = async () => {
    try { await cachedGet("/incidents", null, (data) => setRows(data)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const stopPlayback = () => {
    if (audio) { try { audio.pause(); audio.src = ""; } catch { /* ignore */ } }
    setAudio(null); setPlayingId(null);
  };

  const playReadout = async (incident) => {
    if (playingId === incident.id) { stopPlayback(); return; }
    stopPlayback();
    setTtsBusy(incident.id);
    try {
      const res = await api.get(`/incidents/${incident.id}/tts`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      a.onended = () => { stopPlayback(); URL.revokeObjectURL(url); };
      a.onerror = () => { stopPlayback(); URL.revokeObjectURL(url); toast.error("Could not play audio."); };
      setAudio(a); setPlayingId(incident.id);
      await a.play();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setTtsBusy(null); }
  };

  const save = async () => {
    try {
      const r = await api.post("/incidents", form);
      toast.success("Logged");
      setOpen(false);
      if (r.data?.id) setRows((prev) => [r.data, ...prev]);
      apiCache.invalidate("/incidents");
      apiCache.invalidate("/notifications");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get("/incidents/pdf", { responseType: "blob" });
      await savePdf(res.data, "incidents.pdf");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="incidents-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Incident Log</h1>
          <p className="text-muted-foreground">High-priority incidents auto-email the plan manager.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} className="rounded-full font-bold gap-2 min-h-[48px]" data-testid="incidents-pdf-btn">
            <Download className="w-4 h-4" /> PDF
          </Button>
          <Button variant="outline" onClick={() => setEmailOpen(true)} className="rounded-full font-bold gap-2 min-h-[48px]" data-testid="incidents-email-btn">
            <Mail className="w-4 h-4" /> Email
          </Button>
          <Button onClick={() => setOpen(true)} className="rounded-full font-bold gap-2 min-h-[48px]" style={{ background: "hsl(var(--coral))", color: "white" }} data-testid="add-incident-btn">
            <Plus className="w-4 h-4" /> Log incident
          </Button>
        </div>
      </header>

      <div className="space-y-3">
        {rows.map((r) => (
          <Card key={r.id} className="rounded-3xl p-5 border-2" data-testid={`incident-card-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`rounded-full font-bold uppercase tracking-wider text-[10px] ${
                    r.priority === "high" ? "bg-destructive text-white" :
                    r.priority === "medium" ? "bg-amber-500 text-white" : "bg-muted"}`}>
                    {r.priority === "high" && <AlertTriangle className="w-3 h-3 mr-1" />}
                    {r.priority}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{r.date} · {r.logged_by}</span>
                </div>
                <h3 className="font-display font-extrabold text-lg mt-1">{r.title}</h3>
                <p className="text-sm mt-1">{r.description}</p>
                {r.action_taken && <p className="text-xs text-muted-foreground mt-2"><b>Action:</b> {r.action_taken}</p>}
              </div>
              {isClient && (
                <div className="flex gap-1 items-center">
                  <Button size="sm" variant="ghost" className="rounded-full"
                    onClick={() => playReadout(r)} disabled={ttsBusy === r.id}
                    data-testid={`tts-${r.id}`}
                    title={playingId === r.id ? "Stop" : "Play readout"}>
                    {playingId === r.id ? <VolumeX className="w-4 h-4" /> :
                     ttsBusy === r.id ? <Volume2 className="w-4 h-4 animate-pulse" /> :
                     <Volume2 className="w-4 h-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full text-destructive"
                    onClick={async () => { if(confirm("Delete?")){await api.delete(`/incidents/${r.id}`); load();} }}
                    data-testid={`delete-incident-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              )}
            </div>
          </Card>
        ))}
        {rows.length === 0 && <Card className="rounded-3xl p-10 text-center text-muted-foreground border-2">No incidents logged.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="incident-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Log new incident</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Date</Label><Input type="date" value={form.date} onChange={upd("date")} className="rounded-xl min-h-[48px]" data-testid="incident-date" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase font-bold tracking-wider flex items-center">
                  Priority
                  <FieldHelp text="High = urgent — your plan manager will be auto-emailed when SMTP is set up. Medium = needs follow-up review. Low = minor, audit only." learnMoreId="incidents" />
                </Label>
                <Select value={form.priority} onValueChange={upd("priority")}>
                  <SelectTrigger className="rounded-xl min-h-[48px]" data-testid="incident-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Title</Label><Input value={form.title} onChange={upd("title")} className="rounded-xl min-h-[48px]" data-testid="incident-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Description</Label><Textarea rows={3} value={form.description} onChange={upd("description")} className="rounded-xl" data-testid="incident-description" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Action taken</Label><Textarea rows={2} value={form.action_taken} onChange={upd("action_taken")} className="rounded-xl" data-testid="incident-action" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full font-bold" style={{ background: "hsl(var(--coral))", color: "white" }} data-testid="save-incident-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailRecipientDialog
        open={emailOpen} onOpenChange={setEmailOpen}
        title="Email incident report"
        description="Pick recipients from your address book or add new ones. The latest incidents PDF will be attached."
        onSend={async (recipients) => {
          const res = await api.get("/incidents/pdf", { responseType: "blob" });
          const result = await shareOrMailtoPdf({
            blob: res.data,
            filename: "incidents.pdf",
            recipients,
            subject: "Incident report",
            body: `Hi,\n\nPlease find the latest incident report attached.\n\nKind regards,\n${user?.full_name || ""}`.trim(),
          });
          if (result === "shared") toast.success("Opened in your email app");
          else if (result === "mailto") toast.success(`PDF saved as “incidents.pdf” in Downloads. Attach it in the email composer that just opened.`, { duration: 12000 });
        }}
      />
    </div>
  );
}
